<template>
  <DashboardChartWrapper
    title="Evolution du chiffre d'affaires"
    :loading="loading"
    :error="error"
    @retry="refresh"
  >
    <template #controls>
      <GroupBySelect v-model="groupBy" />
    </template>
    <Line v-if="chartData" :data="chartData" :options="chartOptions" />
  </DashboardChartWrapper>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js'
import { useDashboard } from '@/composables/useDashboard'
import { useDateRange } from '@/composables/useDateRange'
import { formatCurrency } from './helpers'
import DashboardChartWrapper from './DashboardChartWrapper.vue'
import GroupBySelect from './GroupBySelect.vue'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const { dateParams } = useDateRange()
const groupBy = ref('day')

const params = computed(() => ({
  ...dateParams.value,
  group_by: groupBy.value
}))

const { data, loading, error, refresh } = useDashboard('/dashboard/charts/revenue-trend', params)

const chartData = computed(() => {
  if (!data.value || !Array.isArray(data.value)) return null
  return {
    labels: data.value.map(d => d.label),
    datasets: [
      {
        label: 'CA HT',
        data: data.value.map(d => d.revenue_ht),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59,130,246,0.15)',
        fill: true,
        tension: 0.3
      },
      {
        label: 'TVA',
        data: data.value.map(d => d.tva),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139,92,246,0.15)',
        fill: true,
        tension: 0.3
      },
      {
        label: 'Remises',
        data: data.value.map(d => d.discount),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.1)',
        fill: true,
        tension: 0.3
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { position: 'top', labels: { usePointStyle: true, pointStyle: 'circle' } },
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`
      }
    }
  },
  scales: {
    y: { ticks: { callback: v => formatCurrency(v) }, stacked: false }
  }
}
</script>
